import { useEffect, useMemo } from 'react';
import { AppState, Platform } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useTRPC } from '@/lib/trpc';
import { snapshotFromLeagueWeek } from '@/lib/liveActivityModel';
import { liveActivitySyncAction } from '@/lib/liveActivitySession';
import { syncLiveActivity } from '@/lib/liveActivitySync';
import { getCurrentTime } from '@/utils/timeUtils';

export function LiveActivitySync({ isReady }: { isReady: boolean }) {
  const enabledPlatform = Platform.OS === 'ios';
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const trpc = useTRPC();
  const rawLeagueId = user?.defaultLeagueId ?? user?.leagues?.[0]?.id ?? null;
  const leagueId = rawLeagueId == null ? null : String(rawLeagueId);
  const canQuery = enabledPlatform && isReady && isAuthenticated && Boolean(leagueId);

  const currentWeekQuery = useQuery(
    trpc.nfl.currentWeek.queryOptions(undefined, {
      enabled: canQuery,
      staleTime: 60_000,
    }),
  );
  const weekId = currentWeekQuery.data?.id ? String(currentWeekQuery.data.id) : null;
  const weekEnabled = canQuery && Boolean(weekId);

  const gamesQuery = useQuery(
    trpc.leagues.weekGames.queryOptions(
      { leagueId: leagueId ?? '', weekId: weekId ?? '' },
      {
        enabled: weekEnabled,
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: true,
      },
    ),
  );
  const picksQuery = useQuery(
    trpc.picks.list.queryOptions(
      { leagueId: leagueId ?? '', weekId: weekId ?? '' },
      {
        enabled: weekEnabled,
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: true,
      },
    ),
  );
  const definitionsQuery = useQuery(
    trpc.leagues.propDefinitions.queryOptions(
      { leagueId: leagueId ?? '', weekId: weekId ?? '' },
      { enabled: weekEnabled, staleTime: 60_000 },
    ),
  );
  const evaluationsQuery = useQuery(
    trpc.leagues.propEvaluations.queryOptions(
      { leagueId: leagueId ?? '', weekId: weekId ?? '' },
      {
        enabled: weekEnabled,
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: true,
      },
    ),
  );

  const snapshot = useMemo(() => {
    const weekGames = gamesQuery.data as {
      games?: unknown;
      scoringSystem?: 'oddsBased' | 'totalWins';
    } | undefined;
    if (!weekEnabled || !weekGames || !picksQuery.data) return null;
    const now = new Date(Math.max(getCurrentTime().getTime(), gamesQuery.dataUpdatedAt));
    return snapshotFromLeagueWeek({
      now,
      games: weekGames.games as never,
      picks: picksQuery.data,
      propDefinitions: definitionsQuery.data,
      propEvaluations: evaluationsQuery.data,
      scoringSystem: weekGames.scoringSystem,
    });
  }, [
    definitionsQuery.data,
    evaluationsQuery.data,
    gamesQuery.data,
    gamesQuery.dataUpdatedAt,
    picksQuery.data,
    weekEnabled,
  ]);

  const snapshotReady = weekEnabled && !currentWeekQuery.isLoading && !gamesQuery.isLoading && !picksQuery.isLoading;

  useEffect(() => {
    if (!enabledPlatform || !isReady) return;

    const push = () => {
      const action = liveActivitySyncAction({
        isAuthenticated,
        isAuthLoading,
        snapshotReady,
        snapshot,
      });
      if (action === 'hold') return;
      void syncLiveActivity(action === 'clear' ? null : snapshot);
    };

    push();
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') push();
    });
    return () => subscription.remove();
  }, [
    enabledPlatform,
    isAuthenticated,
    isAuthLoading,
    isReady,
    snapshot,
    snapshotReady,
  ]);

  return null;
}
